/**
 * Race control on a race replay (race-replay.ts): the published analysis package's flag changes,
 * pit-lane calls and penalties, as race control's messages, each shown once the replay's clock
 * passes it. The package times them on its own clock (seconds from its time origin); each is put on
 * the replay's clock from the nearest lap crossing before it, which both clocks time, so a red-flag
 * hold left out of a summed-lap clock does not move the later messages.
 *
 * When the race carries race control's full log (GET /races/:id/control: every message, warnings,
 * incidents noted and "no further action" included), that is shown instead (replayControlFromLog).
 */
import type { PitLaneCall, RaceChartData } from "@shared/types";
import type { FlagEvent, Penalty, RaceAnalysisPackage } from "@shared/race-analysis-package/types";
import type { RaceControlMessage } from "../chart/RaceControl";
import { carKey } from "../chart/car-number";
import { clockText, type ReplayTimeline } from "./race-replay";

/** A message and when it shows on the replay's clock (race s). */
export interface ReplayControlEntry {
  tS: number;
  message: RaceControlMessage;
}

/** The package's clock (s from its origin) to the replay's: from the crossings both clocks time. */
export function replayClockFrom(pkg: Pick<RaceAnalysisPackage, "laps">, data: RaceChartData, tl: ReplayTimeline): (pkgS: number) => number {
  const pairs: [number, number][] = [];
  for (const [id, cols] of Object.entries(pkg.laps?.cars ?? {})) {
    const k = carKey(data, id);
    const car = k !== null ? data.cars[String(k)] : undefined;
    const ends = k !== null ? tl.ends[String(k)] : undefined;
    if (!car || !ends) continue;
    const at = new Map<number, number>();
    car.laps.forEach((l, i) => at.set(l.l, ends[i]));
    cols.lap.forEach((lap, i) => {
      const r = at.get(lap);
      if (r !== undefined && Number.isFinite(cols.endS[i])) pairs.push([cols.endS[i], r]);
    });
  }
  pairs.sort((a, b) => a[0] - b[0]);
  if (!pairs.length) return (s) => s;
  return (s) => {
    let lo = 0;
    let hi = pairs.length;
    while (lo < hi) {
      const m = (lo + hi) >> 1;
      if (pairs[m][0] <= s) lo = m + 1;
      else hi = m;
    }
    const [p, r] = pairs[Math.max(0, lo - 1)];
    return Math.max(0, r + (s - p));
  };
}

const FLAG_TEXT: Partial<Record<FlagEvent["type"], string>> = {
  green: "Green flag",
  fcy: "Full course yellow",
  safety_car: "Safety car",
  red: "Red flag",
  chequered: "Chequered flag",
};

const PENALTY_TEXT: Record<Penalty["penaltyType"], string> = {
  warning: "Warning",
  drive_through: "Drive through",
  stop_and_go: "Stop and go",
  stop_plus: "Stop plus time",
  time_at_stop: "Time at the stop",
  post_race_time: "Time added after the race",
  position: "Places",
  lap: "Laps",
  grid: "Grid places",
  disqualification: "Disqualified",
  other: "Penalty",
};

/** The penalty as race control wrote it ("… - Drive Through"), else from its type and amount. */
function penaltyAction(p: Penalty): string {
  const parts = (p.rawText ?? "").split(/\s+-\s+/);
  if (parts.length >= 3 && parts[parts.length - 1].trim()) return parts[parts.length - 1].trim();
  const base = PENALTY_TEXT[p.penaltyType] ?? "Penalty";
  return p.amountS ? `${base} ${Math.round(p.amountS)} s` : base;
}

/** A message's time as the replay shows it: race time ("3:28:15"), as the header's race clock. */
const atText = (raceS: number): string => clockText(raceS);

/** The package's race control, on the replay's clock, in time order. */
export function replayControl(pkg: Pick<RaceAnalysisPackage, "laps" | "events">, data: RaceChartData, tl: ReplayTimeline): ReplayControlEntry[] {
  const toReplay = replayClockFrom(pkg, data, tl);
  const out: { tS: number; m: Omit<RaceControlMessage, "seq"> }[] = [];
  for (const f of pkg.events?.flags ?? []) {
    const note = f.type === "pits_open" || f.type === "pits_closed" || f.type === "message" ? (f.message ?? "").trim() : FLAG_TEXT[f.type];
    if (!note) continue;
    const tS = toReplay(f.at.raceTimeS);
    out.push({ tS, m: { at: atText(tS), cars: [], corner: null, note, status: null, action: null } });
  }
  for (const p of pkg.events?.penalties ?? []) {
    if (!p.issued) continue;
    const tS = toReplay(p.issued.raceTimeS);
    out.push({ tS, m: { at: atText(tS), cars: [String(p.car)], corner: null, note: p.reason || "Penalty", status: null, action: penaltyAction(p) } });
  }
  out.sort((a, b) => a.tS - b.tS);
  return out.map((x, i) => ({ tS: x.tS, message: { seq: i + 1, ...x.m } }));
}

/** An entry of race control's full log as the server keeps it. */
export interface RaceControlLogEntry {
  raceTimeS: number | null;
  clock: string | null;
  leaderLap: number | null;
  text: string;
  cars: string[];
}

/** The cars a message starts with ("Car 7:", "CAR 7 -", "#7 / #12:"), shown as chips instead. */
const LEAD_CARS = /^(?:CARS?\s*#?\d{1,3}[A-Z]?(?:\s*(?:,|&|\/|AND)\s*#?\d{1,3}[A-Z]?)*|#\d{1,3}[A-Z]?(?:\s*\/\s*#\d{1,3}[A-Z]?)*)\s*[:\-–]\s*/i;

/** A logged message as the panel shows it: the cars as chips, the decision ("Drive Through", "Warning") apart. */
export function logMessage(e: Pick<RaceControlLogEntry, "text" | "cars">): { note: string; action: string | null } {
  const body = e.cars.length ? e.text.replace(LEAD_CARS, "") : e.text;
  const parts = body.split(/\s+-\s+/);
  if (e.cars.length && parts.length >= 2 && parts[parts.length - 1].trim()) {
    return { note: parts.slice(0, -1).join(" - ").trim(), action: parts[parts.length - 1].trim() };
  }
  return { note: body.trim() || e.text, action: null };
}

/** Race control's full log on the replay's clock, in time order (untimed messages left out). */
export function replayControlFromLog(log: RaceControlLogEntry[], pkg: Pick<RaceAnalysisPackage, "laps"> | null, data: RaceChartData, tl: ReplayTimeline): ReplayControlEntry[] {
  const toReplay = pkg ? replayClockFrom(pkg, data, tl) : (s: number) => Math.max(0, s);
  const out: ReplayControlEntry[] = [];
  for (const e of log) {
    if (e.raceTimeS === null || !e.text) continue;
    const tS = toReplay(e.raceTimeS);
    const { note, action } = logMessage(e);
    out.push({ tS, message: { seq: 0, at: atText(tS), cars: e.cars, corner: null, note, status: null, action } });
  }
  out.sort((a, b) => a.tS - b.tS);
  out.forEach((x, i) => (x.message.seq = i + 1));
  return out;
}

/** The messages shown at replay time t, newest first (as the live log is). */
export function controlAt(list: ReplayControlEntry[], t: number): RaceControlMessage[] {
  const out: RaceControlMessage[] = [];
  for (const e of list) if (e.tS <= t) out.push(e.message);
  return out.reverse();
}

/** The flag the live status would show, from a flag change in the package. */
const STATUS_FLAG: Partial<Record<FlagEvent["type"], string>> = { green: "Green", fcy: "Yellow", safety_car: "Yellow", red: "Red", chequered: "Checkered" };

/** A flag change on the replay's clock. */
export interface ReplayFlagChange {
  tS: number;
  flag: string;
}

/** The package's flag changes (green, caution, red, chequered) on the replay's clock, in time order. */
export function replayFlags(pkg: Pick<RaceAnalysisPackage, "laps" | "events">, data: RaceChartData, tl: ReplayTimeline): ReplayFlagChange[] {
  const toReplay = replayClockFrom(pkg, data, tl);
  const out: ReplayFlagChange[] = [];
  for (const f of pkg.events?.flags ?? []) {
    const flag = STATUS_FLAG[f.type];
    if (flag) out.push({ tS: toReplay(f.at.raceTimeS), flag });
  }
  return out.sort((a, b) => a.tS - b.tS);
}

/** The flag at replay time t: the last change by then (green before the first); null without changes. */
export function flagAt(changes: ReplayFlagChange[], t: number): string | null {
  if (!changes.length) return null;
  let flag = "Green";
  for (const c of changes) {
    if (c.tS > t) break;
    flag = c.flag;
  }
  return flag;
}

/** A pit-lane call (data.pitLane's states) on the replay's clock. */
export interface ReplayPitCall {
  tS: number;
  state: PitLaneCall["state"];
}

/** The pit-lane state a "pits open" call for these classes is (empty: all cars). */
function openState(classes: string[]): PitLaneCall["state"] | null {
  if (!classes.length) return "open_all";
  const c = classes.join(" ").toUpperCase();
  if (/PROTO|GTP|LMP|DPI/.test(c)) return "open_prototype";
  if (/\bGT/.test(c)) return "open_gt";
  if (/\bGS\b/.test(c)) return "open_gs";
  if (/\bTCR\b/.test(c)) return "open_tcr";
  return null;
}

/** The package's cautions and pit-lane calls on the replay's clock, in time order: the pit lane by the second, not by the lap. */
export function replayPitCalls(pkg: Pick<RaceAnalysisPackage, "laps" | "events">, data: RaceChartData, tl: ReplayTimeline): ReplayPitCall[] {
  const toReplay = replayClockFrom(pkg, data, tl);
  const out: ReplayPitCall[] = [];
  for (const f of pkg.events?.flags ?? []) {
    const state: PitLaneCall["state"] | null =
      f.type === "fcy" || f.type === "safety_car" ? "yellow" : f.type === "pits_closed" ? "closed" : f.type === "pits_open" ? openState(f.classes ?? []) : null;
    if (state) out.push({ tS: toReplay(f.at.raceTimeS), state });
  }
  return out.sort((a, b) => a.tS - b.tS);
}

/**
 * The race's pit-lane calls by replay time t, for the cut chart: the timed ones passed, each given
 * the leader's lap now; race control's "short yellow" calls (in the chart, by lap) kept after the
 * caution they belong to.
 */
export function pitLaneAt(calls: ReplayPitCall[], chart: PitLaneCall[] | undefined, leaderLap: number, t: number): PitLaneCall[] {
  const out: PitLaneCall[] = [];
  for (const c of calls) if (c.tS <= t) out.push({ lap: leaderLap, state: c.state });
  // A short yellow (by lap, from the chart): after the last caution at or before its lap.
  for (const x of chart ?? []) {
    if (x.state !== "short" || x.lap > leaderLap) continue;
    const yellows = (chart ?? []).filter((y) => y.state === "yellow" && y.lap <= x.lap).length;
    let seen = 0;
    let at = out.length;
    for (let i = 0; i < out.length; i++) {
      if (out[i].state === "yellow" && ++seen === yellows) {
        at = i + 1;
        break;
      }
    }
    out.splice(at, 0, { lap: x.lap, state: "short" });
  }
  return out;
}
